
import { Head, Link } from '@inertiajs/react';
import DashboardLayout from '../../../components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

interface Product {
  id: number;
  name: string;
  price: number;
  discount_percentage: number;
  discounted_price: number;
  savings_amount: number;
  category: {
    name: string;
  };
  brand?: {
    name: string;
  };
}

interface Promotion {
  id: number;
  name: string;
  description?: string;
  promotion_type: string;
  banner_text?: string;
  start_date: string;
  end_date: string;
  active: boolean;
  countdown_enabled: boolean;
  countdown_end?: string;
  products_count: number;
  average_discount: number;
  countdown_time_remaining?: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  image_url?: string;
  products: Product[];
}

interface ShowProps {
  promotion: Promotion;
  store_logo?: string;
  store_name?: string;
}

const Show = ({ promotion, store_logo, store_name }: ShowProps) => {
  return (
    <DashboardLayout storeLogo={store_logo} storeName={store_name}>
      <Head title={`Promoción: ${promotion.name}`} />
      
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" asChild>
              <Link href="/admin/promotions">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </Link>
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{promotion.name}</h1>
              <p className="text-muted-foreground">
                Detalles de la promoción
              </p>
            </div>
          </div>
          <Button asChild>
            <Link href={`/admin/promotions/${promotion.id}/edit`}>
              Editar
            </Link>
          </Button>
        </div>

        {/* Información básica */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Información de la Promoción</h2>

          {promotion.image_url && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-500 mb-2">Imagen</p>
              <img
                src={promotion.image_url}
                alt={promotion.name}
                className="w-32 h-32 object-cover rounded-lg border"
              />
            </div>
          )}
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Nombre</p>
              <p className="text-lg">{promotion.name}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Tipo</p>
              <p className="text-lg">{promotion.promotion_type}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Estado</p>
              <p className="text-lg">{promotion.active ? 'Activa' : 'Inactiva'}</p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-gray-500">Productos</p>
              <p className="text-lg">{promotion.products_count}</p>
            </div>
          </div>

          {promotion.description && (
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-500">Descripción</p>
              <p className="text-gray-700">{promotion.description}</p>
            </div>
          )}
        </div>

        {/* Lista de productos */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Productos en la Promoción</h2>
          
          {promotion.products && promotion.products.length > 0 ? (
            <div className="space-y-2">
              {promotion.products.map((product) => (
                <div key={product.id} className="flex justify-between items-center py-2 border-b">
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-sm text-gray-500">{product.category.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">S/. {Number(product.price || 0).toFixed(2)}</p>
                    <p className="text-sm text-red-600">-{Number(product.discount_percentage || 0).toFixed(1)}%</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No hay productos en esta promoción</p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Show;